import type { AchievementKind } from '@busy/api';
import { badgeArtworkUrl } from './badgeArtwork';

// Renders an achievement badge to a 1080×1080 PNG suitable for iMessage,
// Instagram stories, etc.
//
// Two paths:
//   - Artwork present (Busy art for this milestone) → full-bleed artwork
//     hero with a bottom gradient strip carrying the milestone text. Same
//     design language as the in-app chip + detail modal.
//   - Artwork absent (still-pending badges) → legacy gradient hero with
//     canvas-drawn medallion + kind icon. Falls back gracefully.
//
// All-canvas-2d, no DOM rendering — keeps the bundle lean and the output
// pixel-stable across platforms.

type Args = {
  kind: AchievementKind;
  threshold: number; // raw threshold so we can look up matching artwork
  milestone: string; // already-localized, e.g. "100 clients"
  sub: string;       // already-localized, e.g. "You're filling your roster."
  gradient: string;  // ACHIEVEMENT_CATALOG[kind].gradient — used by fallback
};

const SIZE = 1080;

// Palette per kind — mirrors ACHIEVEMENT_CATALOG.gradient so the share image
// matches what the user sees in the celebration modal. We don't try to parse
// Tailwind classes; just hard-code the corresponding hex pairs.
const GRADIENTS: Record<AchievementKind, [string, string]> = {
  streak:          ['#dc2626', '#f97316'],   // red-600 → orange-500
  clients:         ['#131e3f', '#0c1530'],   // navy-800 → navy-900
  cuts:            ['#d4a942', '#e8c66a'],   // gold-500 → gold-400
  revenue:         ['#16a34a', '#15803d'],   // green-600 → green-700
  lessons:         ['#1a2954', '#9c0c23'],   // navy-700 → red-700
  transformations: ['#dc2626', '#d4a942'],   // red-600 → gold-500
  timekeeper:      ['#059669', '#1a2954'],   // emerald-600 → navy-700
};

const ICON_COLORS: Record<AchievementKind, string> = {
  streak:          '#fff7ed',
  clients:         '#f3dc99',
  cuts:            '#0c1530',
  revenue:         '#dcfce7',
  lessons:         '#f3dc99',
  transformations: '#ffffff',
  timekeeper:      '#f3dc99',
};

export async function renderBadgeImage(args: Args): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2d context unavailable');

  // Artwork-first path — if the milestone has Busy art, that's the share
  // hero. Layout mirrors the in-app chip: full-bleed image with a bottom
  // gradient that carries the milestone text + wordmark.
  const artworkUrl = badgeArtworkUrl(args.kind, args.threshold);
  if (artworkUrl) {
    try {
      const img = await loadImage(artworkUrl);
      drawCovered(ctx, img, 0, 0, SIZE, SIZE);
      // Bottom gradient overlay (~45% of canvas) — fades the artwork into
      // a near-opaque navy strip so the text reads cleanly.
      const overlay = ctx.createLinearGradient(0, SIZE * 0.55, 0, SIZE);
      overlay.addColorStop(0, 'rgba(0,0,0,0)');
      overlay.addColorStop(0.5, 'rgba(12,21,48,0.72)');
      overlay.addColorStop(1, 'rgba(12,21,48,0.94)');
      ctx.fillStyle = overlay;
      ctx.fillRect(0, SIZE * 0.55, SIZE, SIZE * 0.45);
      // Eyebrow — gold
      ctx.fillStyle = '#e8c66a';
      ctx.font = 'bold 28px system-ui, -apple-system, "Segoe UI", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      drawSpacedText(ctx, 'MILESTONE UNLOCKED', SIZE / 2, SIZE - 270, 6);
      // Milestone headline
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 96px Georgia, "Times New Roman", serif';
      ctx.textBaseline = 'middle';
      fitText(ctx, args.milestone, SIZE / 2, SIZE - 175, SIZE - 120);
      // Sub
      ctx.fillStyle = 'rgba(255,255,255,0.88)';
      ctx.font = '34px system-ui, -apple-system, sans-serif';
      ctx.textBaseline = 'top';
      wrapText(ctx, args.sub, SIZE / 2, SIZE - 120, SIZE - 200, 44, 2);
      // Wordmark footer — same as the gradient path
      ctx.fillStyle = 'rgba(232,198,106,0.85)';
      ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
      ctx.textBaseline = 'bottom';
      drawSpacedText(ctx, 'BUSY BARBER', SIZE / 2, SIZE - 40, 5);
      return await canvasToBlob(canvas, 'image/png');
    } catch {
      // Artwork load failed (network / missing file) → fall through to
      // the gradient + medallion path so share never fails entirely.
    }
  }

  // Fallback path — gradient + canvas-drawn medallion + icon.

  // Background gradient
  const [c1, c2] = GRADIENTS[args.kind] ?? GRADIENTS.clients;
  const grad = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  grad.addColorStop(0, c1);
  grad.addColorStop(1, c2);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Soft glow behind the medallion
  const glow = ctx.createRadialGradient(SIZE / 2, SIZE / 2 - 80, 60, SIZE / 2, SIZE / 2 - 80, 480);
  glow.addColorStop(0, 'rgba(255,255,255,0.18)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, SIZE, SIZE);

  // Confetti dots — same color palette as BadgeConfetti, but static
  drawConfettiDots(ctx);

  // Eyebrow ("MILESTONE UNLOCKED")
  ctx.fillStyle = 'rgba(255,255,255,0.78)';
  ctx.font = 'bold 28px system-ui, -apple-system, "Segoe UI", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  // Letter-spacing isn't directly supported on canvas — fake it by spacing chars.
  drawSpacedText(ctx, 'MILESTONE UNLOCKED', SIZE / 2, 110, 6);

  // Medallion — rounded square with semi-transparent fill, big icon centered
  drawMedallion(ctx, args.kind);

  // Milestone headline
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 96px Georgia, "Times New Roman", serif';
  ctx.textBaseline = 'middle';
  fitText(ctx, args.milestone, SIZE / 2, 700, SIZE - 120);

  // Subtitle
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = '36px system-ui, -apple-system, sans-serif';
  ctx.textBaseline = 'top';
  wrapText(ctx, args.sub, SIZE / 2, 790, SIZE - 200, 48, 3);

  // Wordmark footer
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = 'bold 26px system-ui, -apple-system, sans-serif';
  ctx.textBaseline = 'bottom';
  drawSpacedText(ctx, 'BUSY BARBER', SIZE / 2, SIZE - 60, 5);

  return await canvasToBlob(canvas, 'image/png');
}

/* ============================ helpers ============================ */

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('canvas.toBlob failed'))), type, 0.92);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Object-cover draw — picks the larger scale so the dest is fully filled,
 *  crops the overflow centered. Used to render square artwork into the share
 *  canvas without letterboxing. */
function drawCovered(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dWidth: number,
  dHeight: number,
) {
  const scale = Math.max(dWidth / img.width, dHeight / img.height);
  const sw = dWidth / scale;
  const sh = dHeight / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dWidth, dHeight);
}

function drawSpacedText(ctx: CanvasRenderingContext2D, text: string, cx: number, y: number, spacing: number) {
  // Fake letter-spacing by measuring + offsetting each char.
  const widths = [...text].map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((s, w) => s + w, 0) + spacing * (text.length - 1);
  let x = cx - total / 2;
  for (let i = 0; i < text.length; i++) {
    ctx.fillText(text[i] ?? '', x + (widths[i] ?? 0) / 2, y);
    x += (widths[i] ?? 0) + spacing;
  }
}

function fitText(ctx: CanvasRenderingContext2D, text: string, cx: number, cy: number, maxWidth: number) {
  // Shrink font size if necessary so text fits within maxWidth.
  let size = 96;
  ctx.font = `bold ${size}px Georgia, "Times New Roman", serif`;
  while (ctx.measureText(text).width > maxWidth && size > 36) {
    size -= 4;
    ctx.font = `bold ${size}px Georgia, "Times New Roman", serif`;
  }
  ctx.fillText(text, cx, cy);
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  yStart: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
) {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
      if (lines.length >= maxLines - 1) break;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  // Truncate the final line with an ellipsis if there's overflow.
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1] ?? '';
    while (ctx.measureText(last + '…').width > maxWidth && last.length > 0) {
      last = last.slice(0, -1);
    }
    lines[maxLines - 1] = last + '…';
  }
  lines.forEach((line, i) => {
    ctx.fillText(line, cx, yStart + i * lineHeight);
  });
}

function drawMedallion(ctx: CanvasRenderingContext2D, kind: AchievementKind) {
  const size = 320;
  const x = SIZE / 2 - size / 2;
  const y = 280;
  const radius = 56;

  // Glassy backdrop
  ctx.save();
  roundRect(ctx, x, y, size, size, radius);
  ctx.fillStyle = 'rgba(255,255,255,0.15)';
  ctx.fill();
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.stroke();
  ctx.restore();

  // Icon
  ctx.save();
  ctx.translate(SIZE / 2, y + size / 2);
  const iconSize = 200;
  ctx.translate(-iconSize / 2, -iconSize / 2);
  ctx.strokeStyle = ICON_COLORS[kind];
  ctx.fillStyle = ICON_COLORS[kind];
  ctx.lineWidth = 14;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  drawKindIcon(ctx, kind, iconSize);
  ctx.restore();
}

// Reproduces the SVG paths used by KindIcon — viewBox is 0 0 24 24 so we
// scale by size/24.
function drawKindIcon(ctx: CanvasRenderingContext2D, kind: AchievementKind, size: number) {
  const s = size / 24;
  ctx.scale(s, s);
  switch (kind) {
    case 'streak': {
      // Flame — filled.
      ctx.beginPath();
      ctx.moveTo(8.5, 14.5);
      ctx.bezierCurveTo(11, 14.5, 11, 12, 11, 12);
      ctx.bezierCurveTo(11, 10.62, 10.5, 10, 10, 9);
      ctx.bezierCurveTo(8.928, 6.857, 9.776, 4.946, 12, 3);
      ctx.bezierCurveTo(12.5, 5.5, 14, 7.9, 16, 9.5);
      ctx.bezierCurveTo(18, 11.1, 19, 13, 19, 15);
      ctx.arc(12, 15, 7, 0, Math.PI * 2);
      ctx.bezierCurveTo(5, 15, 5, 13.847, 5, 12);
      ctx.bezierCurveTo(5.433, 11.294, 6, 11, 6, 11);
      ctx.bezierCurveTo(7.5, 14.5, 8.5, 14.5, 8.5, 14.5);
      ctx.fill();
      break;
    }
    case 'clients': {
      // Two figures — strokes
      ctx.beginPath();
      ctx.moveTo(17, 21);
      ctx.lineTo(17, 19);
      ctx.bezierCurveTo(17, 17, 15, 15, 13, 15);
      ctx.lineTo(5, 15);
      ctx.bezierCurveTo(3, 15, 1, 17, 1, 19);
      ctx.lineTo(1, 21);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(9, 7, 4, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(23, 21);
      ctx.lineTo(23, 19);
      ctx.bezierCurveTo(23, 17, 21, 16, 20, 15.13);
      ctx.stroke();
      break;
    }
    case 'cuts': {
      // Scissors — circles + lines
      ctx.beginPath();
      ctx.arc(6, 6, 3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(6, 18, 3, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(20, 4);
      ctx.lineTo(8.12, 15.88);
      ctx.moveTo(14.47, 14.48);
      ctx.lineTo(20, 20);
      ctx.moveTo(8.12, 8.12);
      ctx.lineTo(12, 12);
      ctx.stroke();
      break;
    }
    case 'revenue': {
      // Dollar
      ctx.beginPath();
      ctx.moveTo(12, 1);
      ctx.lineTo(12, 23);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(17, 5);
      ctx.lineTo(9.5, 5);
      ctx.bezierCurveTo(7.567, 5, 6, 6.567, 6, 8.5);
      ctx.bezierCurveTo(6, 10.433, 7.567, 12, 9.5, 12);
      ctx.lineTo(14.5, 12);
      ctx.bezierCurveTo(16.433, 12, 18, 13.567, 18, 15.5);
      ctx.bezierCurveTo(18, 17.433, 16.433, 19, 14.5, 19);
      ctx.lineTo(6, 19);
      ctx.stroke();
      break;
    }
    case 'lessons': {
      // Book
      ctx.beginPath();
      ctx.moveTo(4, 19.5);
      ctx.bezierCurveTo(4, 18.119, 5.119, 17, 6.5, 17);
      ctx.lineTo(20, 17);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(6.5, 2);
      ctx.lineTo(20, 2);
      ctx.lineTo(20, 22);
      ctx.lineTo(6.5, 22);
      ctx.bezierCurveTo(5.119, 22, 4, 20.881, 4, 19.5);
      ctx.lineTo(4, 4.5);
      ctx.bezierCurveTo(4, 3.119, 5.119, 2, 6.5, 2);
      ctx.stroke();
      break;
    }
    case 'transformations': {
      // Camera body + lens
      ctx.beginPath();
      ctx.moveTo(3, 6);
      ctx.lineTo(7, 6);
      ctx.lineTo(9, 3);
      ctx.lineTo(15, 3);
      ctx.lineTo(17, 6);
      ctx.lineTo(21, 6);
      ctx.bezierCurveTo(22.105, 6, 23, 6.895, 23, 8);
      ctx.lineTo(23, 19);
      ctx.bezierCurveTo(23, 20.105, 22.105, 21, 21, 21);
      ctx.lineTo(3, 21);
      ctx.bezierCurveTo(1.895, 21, 1, 20.105, 1, 19);
      ctx.lineTo(1, 8);
      ctx.bezierCurveTo(1, 6.895, 1.895, 6, 3, 6);
      ctx.closePath();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(12, 13, 4, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
    case 'timekeeper': {
      // Clock — circle + hands
      ctx.beginPath();
      ctx.arc(12, 12, 10, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(12, 6);
      ctx.lineTo(12, 12);
      ctx.lineTo(16, 14);
      ctx.stroke();
      break;
    }
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const DOT_PALETTE = ['#d4a942', '#e8c66a', '#f3dc99', '#c8102e', '#22c55e', '#ffffff'];

function drawConfettiDots(ctx: CanvasRenderingContext2D) {
  // Deterministic-ish spray using a seeded PRNG so repeated renders look
  // the same shape — not literally, but the visual density is consistent.
  let seed = 1337;
  function rnd() {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  }
  ctx.save();
  ctx.globalAlpha = 0.22;
  for (let i = 0; i < 60; i++) {
    const x = rnd() * SIZE;
    const y = rnd() * SIZE;
    const w = 6 + rnd() * 14;
    const h = 4 + rnd() * 10;
    const rot = rnd() * Math.PI * 2;
    ctx.fillStyle = DOT_PALETTE[Math.floor(rnd() * DOT_PALETTE.length)] ?? '#ffffff';
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rot);
    ctx.fillRect(-w / 2, -h / 2, w, h);
    ctx.restore();
  }
  ctx.restore();
}
